import React, {
  useEffect,
  useState,
  lazy,
  Suspense,
  useCallback,
  useRef,
} from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./utils/i18n";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Outlet,
} from "react-router-dom";

// Auth
import PrivateRoutes from "./utils/auth_provider";
import AdminRoutes from "./utils/admin_auth_provider";
import PublicRoutes from "./utils/public_auth";
import api, { getToken } from "./utils/Api";

// Context & Components
import { ProfilePictureProvider } from "./context/ProfilePictureContext";
import { GradientSpinner } from "src/components/Loader";
import reportWebVitals from "./reportWebVitals";

import { TooltipProvider } from "./components/ui/tooltip";


// ============================================
// LAZY WITH RETRY FUNCTION
// ============================================
const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.localStorage.getItem("page-has-been-force-refreshed") || "false"
    );

    try {
      const component = await componentImport();
      window.localStorage.setItem("page-has-been-force-refreshed", "false");
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        // Mark that we've force refreshed once
        window.localStorage.setItem("page-has-been-force-refreshed", "true");
        // Force a page refresh to get the latest chunks
        window.location.reload();
        // Return a never-resolving promise to keep React waiting
        return new Promise(() => {});
      }
      // If we've already refreshed once, throw the error
      throw error;
    }
  });

// ============================================
// LAZY COMPONENTS
// ============================================
const UserLogin = lazy(() => import("./views/loginForm/userLogin.jsx"));
const AdminLogin = lazy(() => import("./views/loginForm/adminLogin"));
const Layout = lazy(() => import("./views/layout/Layout"));
const AdminLayout = lazy(() => import("./views/layout/adminLayout"));
const MyBooking = lazy(() => import("./views/myBookings/mybooking"));
const Courses = lazy(() => import("./views/courses/courses.jsx"));
const Recommendation = lazy(() => import("./views/recommendation/recom"));
const DashboardGraph = lazy(() => import("./views/dashboard/progressGraph"));
const ProfilePage = lazy(() => import("./views/profile/profile"));
const Dash = lazy(() => import("./views/Dash/dash.js"));
const Mentor = lazy(() => import("./views/mentors/mentors.jsx"));
const VerifyEmail = lazy(() => import("./views/loginForm/forgotPassword.jsx"));
const VerifyOtp = lazy(() => import("./views/loginForm/otpVerification.jsx"));
const SetPassword = lazy(() => import("./views/loginForm/resetPassword.jsx"));
const ScheduledEvents = lazy(() => import("./views/Diary/ScheduleEvents"));
const MentorshipDiary = lazy(() => import("./views/Diary/MentorshipDiary"));
const MentorProfile = lazy(() => import("./components/mentorProfile"));
const AdminDashboard = lazy(() => import("./views/admin/dash.js"));
const Job = lazy(() => import("./views/admin/job/job.js"));
const JobInfo = lazy(() => import("./views/admin/job/Jobinfo"));
const AdminCourse = lazy(() => import("./views/admin/AdminCourse/Admincourse"));
const AdminFeedback = lazy(() =>
  import("./views/admin/feedback/Admin_Feedback")
);
const EmployeeDetails = lazy(() => import("./views/admin/employeeDetails"));
const Team = lazy(() => import("./views/Team/team"));
const Companies = lazy(() => import("./views/admin/Organisations/companies"));
const UsersManagement = lazy(() =>
  import("./views/admin/User/UsersManagement")
);
const Services = lazy(() => import("./views/admin/Services/Services"));
const Career_navigator = lazy(() =>
  import("./views/careerplan/career_navigator.jsx")
);
const CareerNavigatorPage = lazy(() =>
  import("./views/careerplan/careerNavigatorPage.jsx")
);
const TwinAILandingPage = lazyWithRetry(() =>
  import("./views/TwinAI/TwinAILandingPage")
);
const CreateTwin = lazyWithRetry(() => import("./views/TwinAI/CreateTwin"));
const ChatwithTwin = lazyWithRetry(() => import("./views/TwinAI/ChatwithTwin"));
const ReportIssuePage = lazy(() =>
  import("./views/admin/reportIssues/reportIssuePage")
);

// Dynamic service components
const SERVICE_COMPONENTS = {
  Calendar: lazy(() => import("./views/calendar/calendar")),
  Sponsor: lazy(() => import("./views/sponsor/sponsor.jsx")),
  Progress: lazy(() => import("./views/Progress/progress")),
  Admiration: lazy(() => import("./views/Achievement/achievement")),
  CareerPlan: lazy(() => import("./views/careerplan/careerplan.js")),
};

// ============================================
// UI COMPONENTS
// ============================================
const Spinner = () => (
  <div className="flex justify-center items-center h-24">
    <GradientSpinner />
  </div>
);

const FullPageLoader = ({ message = "Loading..." }) => (
  <div className="flex justify-center items-center h-screen">
    <div className="text-center">
      <GradientSpinner />
      <p className="mt-4 text-gray-600">{message}</p>
    </div>
  </div>
);

const NotFound = () => (
  <div className="flex flex-col justify-center items-center h-screen text-center">
    <h1 className="text-5xl font-bold mb-4">404</h1>
    <p className="text-xl">Page Not Found</p>
  </div>
);

const ErrorPage = ({ message, onRetry }) => (
  <div className="flex flex-col justify-center items-center h-screen text-center">
    <h2 className="text-2xl font-bold mb-4 text-red-600">
      Something went wrong
    </h2>
    <p className="text-gray-600 mb-4">{message}</p>
    <button
      onClick={onRetry}
      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
    >
      Try Again
    </button>
  </div>
);

// ============================================
// LAYOUT WRAPPERS
// ============================================
const UserLayout = React.memo(() => (
  <ProfilePictureProvider>
    <Layout>
      <Suspense fallback={<Spinner />}>
        <Outlet />
      </Suspense>
    </Layout>
  </ProfilePictureProvider>
));
UserLayout.displayName = "UserLayout";

const AdminLayoutWrapper = React.memo(() => (
  <ProfilePictureProvider>
    <AdminLayout>
      <Suspense fallback={<Spinner />}>
        <Outlet />
      </Suspense>
    </AdminLayout>
  </ProfilePictureProvider>
));
AdminLayoutWrapper.displayName = "AdminLayoutWrapper";

// ============================================
// DYNAMIC ROUTES HOOK
// ============================================
function useDynamicRoutes() {
  const [routes, setRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false); // NEW: Track initial load
  const hasFetched = useRef(false);
  const intervalRef = useRef(null);

  const fetchRoutes = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setRoutes([]);
      hasFetched.current = false;
      setInitialLoadComplete(true); // Mark as complete even without token
      return;
    }

    if (hasFetched.current) return;
    hasFetched.current = true;

    setIsLoading(true);
    setError(null);

    try {
      // Use api instance - auth errors handled automatically
      const response = await api.post(
        "/admin/service_integration/get_service_check_bulk",
        { token }
      );

      const data = response.data;

      if (!Array.isArray(data)) {
        throw new Error("Invalid response");
      }

      const routeElements = data
        .filter((s) => SERVICE_COMPONENTS[s.service_name])
        .map((service) => {
          const Component = SERVICE_COMPONENTS[service.service_name];
          return (
            <Route
              key={service.service_id}
              path={service.service_name}
              element={
                <Suspense fallback={<Spinner />}>
                  <Component />
                </Suspense>
              }
            />
          );
        });

      setRoutes(routeElements);
      setInitialLoadComplete(true); // NEW: Mark initial load as complete
    } catch (err) {
      // Auth errors redirect automatically via api.js
      // Only set error for non-auth errors
      if (err.message !== "Token expired") {
        console.error("[Routes] Fetch error:", err);
        setError(err.message);
      }
      setInitialLoadComplete(true); // NEW: Mark as complete even on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoutes();

    // FIX: Poll for token changes since storage event doesn't fire in same window
    intervalRef.current = setInterval(() => {
      const token = getToken();
      const hasToken = Boolean(token);
      const hasRoutes = routes.length > 0;

      // If we have a token but no routes, and haven't fetched yet, fetch now
      if (hasToken && !hasRoutes && !hasFetched.current) {
        console.log("[Routes] Token detected, fetching routes...");
        fetchRoutes();
      }

      // If token is removed, clear routes
      if (!hasToken && hasRoutes) {
        console.log("[Routes] Token removed, clearing routes");
        setRoutes([]);
        hasFetched.current = false;
      }
    }, 500); // Check every 500ms

    // Also keep storage listener for cross-tab sync
    const onStorage = (e) => {
      if (e.key === "user_tk") {
        hasFetched.current = false;
        fetchRoutes();
      }
    };

    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchRoutes, routes.length]);

  return {
    routes,
    isLoading,
    error,
    initialLoadComplete, // NEW: Export this flag
    retry: () => {
      hasFetched.current = false;
      setInitialLoadComplete(false); // NEW: Reset on retry
      fetchRoutes();
    },
  };
}

// ============================================
// ROUTER FACTORY
// ============================================
function createRouter(dynamicRoutes = []) {
  return createBrowserRouter(
    createRoutesFromElements(
      <>
        {/* PUBLIC */}
        <Route element={<PublicRoutes />}>
          <Route path="/" element={<UserLogin />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/forgot_pass" element={<VerifyEmail />} />
          <Route path="/forgot_pass/:emailInput" element={<VerifyOtp />} />
          <Route
            path="/forgot_pass/:emailInput/generate_pass"
            element={<SetPassword />}
          />
        </Route>

        {/* USER ROUTES */}
        <Route element={<PrivateRoutes />}>
          <Route path="/d" element={<UserLayout />}>
            <Route path="dashboard" element={<Dash />} />
            <Route path="mentor" element={<Mentor />} />
            <Route path="MyBooking" element={<MyBooking />} />
            <Route path="Courses" element={<Courses />} />
            <Route path="recommendation" element={<Recommendation />} />
            <Route path="ScheduledEvents" element={<ScheduledEvents />} />
            <Route path="MentorshipDiary" element={<MentorshipDiary />} />
            <Route path="DashboardGraph" element={<DashboardGraph />} />
            <Route path="Profile" element={<ProfilePage />} />
            <Route path="Team" element={<Team />} />
            <Route path="MentorProfile" element={<MentorProfile />} />
            <Route path="Career_navigator" element={<Career_navigator />} />
            <Route
              path="CareerNavigatorPage"
              element={<CareerNavigatorPage />}
            />
            <Route path="TwinAI" element={<TwinAILandingPage />} />
            <Route path="create-twin" element={<CreateTwin />} />
            <Route path="chat-with-twin" element={<ChatwithTwin />} />
            {dynamicRoutes}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>

        {/* ADMIN ROUTES */}
        <Route element={<AdminRoutes />}>
          <Route path="/a" element={<AdminLayoutWrapper />}>
            <Route path="adminDash" element={<AdminDashboard />} />
            <Route path="Profile" element={<ProfilePage />} />
            <Route path="job" element={<Job />} />
            <Route path="Job/:advertisementId" element={<JobInfo />} />
            <Route path="course" element={<AdminCourse />} />
            <Route path="feedback" element={<AdminFeedback />} />
            <Route path="employees" element={<EmployeeDetails />} />
            <Route path="MentorProfile" element={<MentorProfile />} />
            <Route path="companies" element={<Companies />} />
            <Route path="UsersManagement" element={<UsersManagement />} />
            <Route path="services" element={<Services />} />
            <Route path="report_issues" element={<ReportIssuePage />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </>
    )
  );
}

// ============================================
// APP
// ============================================
function App() {
  const { routes, isLoading, error, initialLoadComplete, retry } =
    useDynamicRoutes();
  const [router, setRouter] = useState(null); // Changed back to null
  const prevRoutesLength = useRef(0);

  // Recreate router when routes change OR initial load completes
  useEffect(() => {
    // NEW: Only create router after initial load is complete
    if (!initialLoadComplete) return;

    if (routes.length !== prevRoutesLength.current || !router) {
      console.log(`[App] Creating router with ${routes.length} dynamic routes`);
      setRouter(createRouter(routes));
      prevRoutesLength.current = routes.length;
    }
  }, [routes, initialLoadComplete, router]);

  const hasToken = Boolean(getToken());

  // if (error && hasToken) {
  //   return <ErrorPage message={error} onRetry={retry} />;
  // }

  // NEW: Show loader until initial load is complete
  if (!initialLoadComplete || !router) {
    return <FullPageLoader message="Loading your services..." />;
  }

  return <RouterProvider router={router} />;
}

// ============================================
// RENDER
// ============================================
ReactDOM.createRoot(document.getElementById("root")).render(<TooltipProvider delayDuration={200}>
    <App />
  </TooltipProvider>);
reportWebVitals();
